'use client';

import * as React from 'react';

import { Button } from '@workspace/ui/components/button';
import { Input } from '@workspace/ui/components/input';
import { toast } from '@workspace/ui/components/sonner';
import { cn } from '@workspace/ui/lib/utils';

import { useMarketingAnalytics } from '~/hooks/use-marketing-analytics';
import {
  hasBrowserWeb3Forms,
  submitBrowserWeb3FormsLead
} from '~/lib/web3forms-client';

type NewsletterSignupFormProps = {
  inputId: string;
  source: string;
  buttonLabel?: string;
  submittingLabel?: string;
  className?: string;
  inputClassName?: string;
  buttonClassName?: string;
};

export function NewsletterSignupForm({
  inputId,
  source,
  buttonLabel = 'Subscribe',
  submittingLabel = 'Submitting...',
  className,
  inputClassName,
  buttonClassName
}: NewsletterSignupFormProps): React.JSX.Element {
  const [isSubscribing, setIsSubscribing] = React.useState(false);
  const formRef = React.useRef<HTMLFormElement>(null);
  const { trackNewsletterSubscribe, trackNewsletterSubscribeFailed } =
    useMarketingAnalytics();

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>
  ): Promise<void> => {
    e.preventDefault();

    setIsSubscribing(true);

    const formData = new FormData(e.currentTarget);
    const email = formData.get('email');
    const botcheck = formData.get('botcheck');
    const subscriberEmail = typeof email === 'string' ? email.trim() : '';
    const botcheckValue = typeof botcheck === 'string' ? botcheck : '';

    try {
      if (botcheckValue) {
        toast.success('Subscribed. We will keep you posted.');
        formRef.current?.reset();
        return;
      }

      if (hasBrowserWeb3Forms()) {
        await submitBrowserWeb3FormsLead({
          formName: 'Viventium newsletter form',
          subject: 'Viventium newsletter subscription',
          fields: {
            email: subscriberEmail,
            botcheck: botcheckValue,
            message: 'subscribed',
            source
          }
        });
      } else {
        const response = await fetch('/api/newsletter', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: subscriberEmail,
            botcheck: botcheckValue,
            source
          })
        });

        const result = (await response.json().catch(() => ({}))) as {
          success?: string | boolean;
        };

        if (
          !response.ok ||
          result.success === false ||
          result.success === 'false'
        ) {
          throw new Error('Failed to subscribe');
        }
      }

      toast.success('Subscribed. We will keep you posted.');
      trackNewsletterSubscribe(source);
      formRef.current?.reset();
    } catch {
      trackNewsletterSubscribeFailed(source);
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setIsSubscribing(false);
    }
  };

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit}
      className={cn('flex w-full flex-col gap-3 sm:flex-row', className)}
      data-testid={`${source}-newsletter-form`}
    >
      <input
        type="text"
        name="botcheck"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
      />
      <label
        htmlFor={inputId}
        className="sr-only"
      >
        Email address
      </label>
      <Input
        id={inputId}
        name="email"
        type="email"
        placeholder="you@company.com"
        required
        className={cn('min-w-0 rounded-full bg-background', inputClassName)}
      />
      <Button
        type="submit"
        className={cn('rounded-full px-6', buttonClassName)}
        disabled={isSubscribing}
      >
        {isSubscribing ? submittingLabel : buttonLabel}
      </Button>
    </form>
  );
}
